import { useMemo } from "react";
import { X, ArrowUpCircle, ArrowDownCircle } from "lucide-react";
import { useI18n } from "@/lib/I18nContext";

/**
 * Phase 17b — Silent-Match trade mirror.
 *
 * Full-viewport modal. Top half: gold background, HAVE numbers. Bottom
 * half: black background, WANT numbers. Numbers are large (≥ 72pt so
 * counterparts can read from across a bench). NO audio, NO speech
 * synthesis — silent by design.
 *
 * `perspective`:
 *   "generator" → HAVE = data.generator_perspective.have (my dupes I give)
 *                 WANT = data.generator_perspective.want (their dupes I take)
 *   "scanner"   → HAVE = data.scanner_perspective.have   (my dupes I give)
 *                 WANT = data.scanner_perspective.want   (their dupes I take)
 *
 * The backend pre-flips both perspectives on `POST /api/trade/scan-qr`.
 */
export default function TradeMirror({ perspective, session, onClose }) {
  const { t } = useI18n();

  const { have, want } = useMemo(() => {
    if (!session) return { have: [], want: [] };
    if (perspective === "generator") {
      return session.generator_perspective || { have: [], want: [] };
    }
    return session.scanner_perspective || { have: [], want: [] };
  }, [perspective, session]);

  const shortId = session?.session_id
    ? session.session_id.slice(0, 8).toUpperCase()
    : "";

  // Legibility scaling — many numbers → step down but never below 48pt.
  const haveScale =
    have.length > 20
      ? "clamp(48pt, 6vw, 72pt)"
      : "clamp(72pt, 10vw, 120pt)";
  const wantScale =
    want.length > 20
      ? "clamp(48pt, 6vw, 72pt)"
      : "clamp(72pt, 10vw, 120pt)";

  return (
    <div
      data-testid="trade-mirror-root"
      data-perspective={perspective}
      className="fixed inset-0 z-[55] flex flex-col"
      role="dialog"
      aria-modal="true"
    >
      {/* HAVE — gold half */}
      <section
        data-testid="trade-mirror-have"
        className="flex-1 flex flex-col items-center justify-center px-6 py-6 text-center"
        style={{ backgroundColor: "#FFD700", color: "#000000" }}
      >
        <div className="flex items-center gap-3 mb-4">
          <ArrowUpCircle className="h-8 w-8" />
          <h2
            data-testid="trade-mirror-have-label"
            className="font-display font-black tracking-tight"
            style={{ fontSize: "clamp(24pt, 4vw, 42pt)", letterSpacing: "0.06em" }}
          >
            {t("trade.have_label")}
          </h2>
        </div>
        <div
          data-testid="trade-mirror-have-numbers"
          className="flex flex-wrap items-center justify-center gap-x-6 gap-y-3 max-w-full font-mono font-black leading-none"
          style={{ fontSize: haveScale }}
        >
          {have.map((n) => (
            <span key={n} data-testid={`trade-have-${n}`}>
              {n}
            </span>
          ))}
        </div>
      </section>

      {/* WANT — black half */}
      <section
        data-testid="trade-mirror-want"
        className="flex-1 flex flex-col items-center justify-center px-6 py-6 text-center"
        style={{ backgroundColor: "#000000", color: "#FFFFFF" }}
      >
        <div className="flex items-center gap-3 mb-4">
          <ArrowDownCircle className="h-8 w-8" style={{ color: "#FFD700" }} />
          <h2
            data-testid="trade-mirror-want-label"
            className="font-display font-black tracking-tight"
            style={{
              fontSize: "clamp(24pt, 4vw, 42pt)",
              letterSpacing: "0.06em",
              color: "#FFD700",
            }}
          >
            {t("trade.want_label")}
          </h2>
        </div>
        <div
          data-testid="trade-mirror-want-numbers"
          className="flex flex-wrap items-center justify-center gap-x-6 gap-y-3 max-w-full font-mono font-black leading-none"
          style={{ fontSize: wantScale }}
        >
          {want.map((n) => (
            <span key={n} data-testid={`trade-want-${n}`}>
              {n}
            </span>
          ))}
        </div>
      </section>

      {/* Session hash tag — top-right */}
      <div
        data-testid="trade-mirror-session-hash"
        className="fixed top-4 right-4 font-mono text-xs text-black/60 bg-white/70 px-2 py-1 rounded-md pointer-events-none"
      >
        #{shortId}
      </div>

      {/* Close — floating bottom-right */}
      <button
        type="button"
        data-testid="trade-mirror-close"
        onClick={onClose}
        aria-label={t("trade.close")}
        className="fixed bottom-6 right-6 h-14 min-w-[44px] px-5 rounded-full bg-white text-black font-body font-bold text-sm shadow-lg hover:bg-neutral-200"
      >
        <span className="inline-flex items-center gap-2">
          <X className="h-5 w-5" />
          {t("trade.close")}
        </span>
      </button>
    </div>
  );
}
